import React, { useEffect } from 'react';

import { Controller, useForm } from 'react-hook-form';

import { PasswordInput } from 'components/common/PasswordInput/PasswordInput';

import { Alert } from 'components/DesignSystem/Alert';
import { Button } from 'components/DesignSystem/Button';
import { TextInput } from 'components/DesignSystem/TextInput';
import { CustomizedFormErrorMessage } from 'components/common';

import classes from './OpcCredentialsForm.module.scss';

export interface OpcCredentialsFormValues {
    opcHost: string;
    opcUsername: string;
    opcPassword: string;
}

interface Props {
    isEditMode?: boolean;
    defaultValues?: Partial<OpcCredentialsFormValues> | null;
    onSubmit?: (values: OpcCredentialsFormValues) => void;
    isLoadingSubmit?: boolean;
}

const REQUIRED_FIELD_ERROR_MESSAGE = 'This field is required';

const CREATE_TITLE = 'How authentication works';
const CREATE_SUBTITLE =
    'We recommend using a dedicated integration account with only the permissions required. Your password is encrypted and will not be shown after saving.';

const EDIT_TITLE = 'Update Oracle Primavera Cloud credentials';
const EDIT_SUBTITLE =
    'Enter updated credentials for the OPC integration account. Saving will replace the current connection details.';

export const OpcCredentialsForm = ({
    isEditMode = false,
    defaultValues = null,
    onSubmit,
    isLoadingSubmit = false,
}: Props) => {
    const form = useForm<OpcCredentialsFormValues>({
        defaultValues: {
            opcHost: '',
            opcUsername: '',
            opcPassword: '',
        },
        mode: 'onSubmit',
    });

    useEffect(() => {
        if (!isEditMode || !defaultValues) {
            return;
        }
        form.reset({
            opcHost: defaultValues.opcHost ?? '',
            opcUsername: defaultValues.opcUsername ?? '',
            opcPassword: defaultValues.opcPassword ?? '',
        });
    }, [isEditMode, defaultValues, form]);

    return (
        <div className={classes.OpcCredentialsForm}>
            <div className={classes.titleWrapper}>
                <div className={classes.title}>{isEditMode ? EDIT_TITLE : CREATE_TITLE}</div>
                <div className={classes.subtitle}>{isEditMode ? EDIT_SUBTITLE : CREATE_SUBTITLE}</div>
            </div>
            <form
                className={classes.form}
                autoComplete={'off'}
                onSubmit={form.handleSubmit((values) => onSubmit?.(values))}
            >
                <div className={classes.fields}>
                    <div className={classes.fieldRow}>
                        <span className={classes.fieldLabel}>OPC Host</span>
                        <Controller
                            control={form.control}
                            name={'opcHost'}
                            rules={{ required: REQUIRED_FIELD_ERROR_MESSAGE }}
                            render={({ field }) => (
                                <TextInput
                                    {...field}
                                    placeholder={'primavera-uk1.oraclecloud.com'}
                                    autoComplete={'off'}
                                    clearable={false}
                                    error={!!form.formState.errors.opcHost}
                                />
                            )}
                        />
                        {form.formState.errors.opcHost && (
                            <CustomizedFormErrorMessage text={form.formState.errors.opcHost.message} />
                        )}
                    </div>

                    <div className={classes.fieldRow}>
                        <span className={classes.fieldLabel}>Integration Account Username</span>
                        <Controller
                            control={form.control}
                            name={'opcUsername'}
                            rules={{ required: REQUIRED_FIELD_ERROR_MESSAGE }}
                            render={({ field }) => (
                                <TextInput
                                    {...field}
                                    placeholder={'name@company.com'}
                                    autoComplete={'off'}
                                    clearable={false}
                                    error={!!form.formState.errors.opcUsername}
                                />
                            )}
                        />
                        {form.formState.errors.opcUsername && (
                            <CustomizedFormErrorMessage text={form.formState.errors.opcUsername.message} />
                        )}
                    </div>

                    <div className={classes.fieldRow}>
                        <span className={classes.fieldLabel}>Password</span>
                        <Controller
                            control={form.control}
                            name={'opcPassword'}
                            rules={{ required: REQUIRED_FIELD_ERROR_MESSAGE }}
                            render={({ field }) => (
                                <PasswordInput
                                    {...field}
                                    autoComplete={'new-password'}
                                    error={!!form.formState.errors.opcPassword}
                                />
                            )}
                        />
                        {form.formState.errors.opcPassword && (
                            <CustomizedFormErrorMessage text={form.formState.errors.opcPassword.message} />
                        )}
                        <Alert className={classes.passwordAlert} severity={'warning'}>
                            This password will be encrypted and never displayed after saving.
                        </Alert>
                    </div>
                </div>

                <div className={classes.buttons}>
                    <Button type={'submit'} variant={'primary'} isLoading={isLoadingSubmit}>
                        {isEditMode ? 'Save credentials' : 'Connect'}
                    </Button>
                </div>
            </form>
        </div>
    );
};
